// Contabilidad › Analítico › PRESUPUESTO.
// Rejilla cuentas × 12 meses de un ejercicio. Los importes van en SALDO NATURAL
// (ingresos y gastos, ambos positivos), igual que el motor de reportes, para que
// el comparativo Real vs. Presupuesto de Inteligencia de negocios cuadre sin
// conversiones. Nadie captura 200 cuentas a mano: se parte del real de otro año
// (o de otro presupuesto) con un factor y se corrige lo que haga falta.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileDown, FileUp, Loader2, Save, Target, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { field, usd, hoy, rpc } from './shared';
import {
  MESES, type CuentaPresu, type ResultadoImport,
  descargarPlantilla, leerPlantilla, num, suma,
} from './presupuestoPlantilla';

const UNIVERSOS = [
  { v: 'resultados', label: 'Ingresos, costos y gastos' },
  { v: 'balance', label: 'Cuentas de balance' },
  { v: 'todo', label: 'Todo el plan' },
];

interface Rejilla { anio: number; universo: string; cuentas: CuentaPresu[] }


function GenerarModal({ anio, onCerrar, onHecho }: {
  anio: number; onCerrar: () => void; onHecho: () => void;
}) {
  const [origen, setOrigen] = useState<'real' | 'presupuesto'>('real');
  const [anioOrigen, setAnioOrigen] = useState(anio - 1);
  const [factor, setFactor] = useState('1.00');
  const [reemplazar, setReemplazar] = useState(true);

  const generar = useMutation({
    mutationFn: () => rpc<{ filas: number }>('cont_presupuesto_generar', {
      _anio_destino: anio, _anio_origen: anioOrigen, _origen: origen,
      _factor: num(factor), _reemplazar: reemplazar,
    }),
    onSuccess: (r) => { toast.success(`Presupuesto generado (${r.filas} importes)`); onHecho(); onCerrar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <PanelIntegrado title={`Generar el presupuesto ${anio}`} onClose={onCerrar}>
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Partir de</span>
          <select className={field} value={origen} onChange={(e) => setOrigen(e.target.value as 'real' | 'presupuesto')}>
            <option value="real">El movimiento REAL de un año</option>
            <option value="presupuesto">El PRESUPUESTO de otro año</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Año de origen</span>
          <input type="number" className={field} value={anioOrigen}
            onChange={(e) => setAnioOrigen(Number(e.target.value))} />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Factor (1.10 = +10%)</span>
          <input className={field} value={factor} onChange={(e) => setFactor(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={reemplazar} onChange={(e) => setReemplazar(e.target.checked)} />
          Reemplazar lo que ya haya en {anio}
        </label>
        <p className="text-xs text-muted-foreground">
          Los importes se calculan en saldo natural, mes a mes. Después puedes corregir a mano lo que necesites.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onCerrar}>Cancelar</Button>
          <Button size="sm" disabled={generar.isPending} onClick={() => generar.mutate()}>
            {generar.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1 h-4 w-4" />}
            Generar
          </Button>
        </div>
      </div>
    </PanelIntegrado>
  );
}

export function Presupuesto() {
  const qc = useQueryClient();
  const [anio, setAnio] = useState(Number(hoy.slice(0, 4)));
  const [universo, setUniverso] = useState('resultados');
  const [filtro, setFiltro] = useState('');
  const [modal, setModal] = useState(false);
  // edición local: cuenta_id → 12 meses
  const [edit, setEdit] = useState<Record<string, number[]>>({});
  const [importando, setImportando] = useState(false);
  const [resImport, setResImport] = useState<ResultadoImport | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isFetching } = useQuery<Rejilla>({
    queryKey: ['presupuesto', anio, universo],
    queryFn: () => rpc<Rejilla>('cont_presupuesto_get', { _anio: anio, _universo: universo }),
  });

  // al recargar la rejilla se descartan las ediciones pendientes
  useEffect(() => { setEdit({}); setResImport(null); }, [data]);

  const cuentas = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    const base = data?.cuentas ?? [];
    return f ? base.filter((c) => `${c.codigo} ${c.nombre}`.toLowerCase().includes(f)) : base;
  }, [data, filtro]);

  const mesesDe = (c: CuentaPresu) => edit[c.cuenta_id] ?? c.meses;
  const setMes = (c: CuentaPresu, i: number, v: number) => {
    const arr = [...mesesDe(c)]; arr[i] = v;
    setEdit((e) => ({ ...e, [c.cuenta_id]: arr }));
  };
  const repartirAnual = (c: CuentaPresu, total: number) => {
    const base = Math.round((total / 12) * 100) / 100;
    const arr = Array(12).fill(base);
    // el ajuste del redondeo va al último mes para que el total cuadre exacto
    arr[11] = Math.round((total - base * 11) * 100) / 100;
    setEdit((e) => ({ ...e, [c.cuenta_id]: arr }));
  };

  const importar = async (file: File) => {
    setImportando(true);
    try {
      // se compara contra TODAS las cuentas de la rejilla, no contra el filtro
      // de búsqueda, para que un filtro activo no descarte filas del archivo
      const { cambios, resumen } = await leerPlantilla(file, data?.cuentas ?? []);
      if (resumen.aplicadas === 0) {
        toast.error('No se pudo casar ninguna cuenta del archivo con el plan de cuentas');
      } else {
        setEdit((e) => ({ ...e, ...cambios }));
        toast.success(`${resumen.aplicadas} cuenta(s) cargadas. Revisa y pulsa Guardar.`);
      }
      setResImport(resumen);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo leer el archivo');
    } finally {
      setImportando(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const sucio = Object.keys(edit).length > 0;
  const guardar = useMutation({
    // la RPC admite 2000 líneas por llamada y un plan completo puede pasarse
    // (sobre todo importando desde Excel); se manda por tandas, que es seguro
    // porque el guardado reemplaza cuenta por cuenta
    mutationFn: async () => {
      const lineas = Object.entries(edit).map(([cuenta_id, meses]) => ({ cuenta_id, meses }));
      for (let i = 0; i < lineas.length; i += 500) {
        await rpc('cont_presupuesto_set', { _anio: anio, _lineas: lineas.slice(i, i + 500) });
      }
    },
    onSuccess: () => {
      toast.success('Presupuesto guardado');
      setEdit({});
      void qc.invalidateQueries({ queryKey: ['presupuesto'] });
      void qc.invalidateQueries({ queryKey: ['bi-ejecutar'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalPresu = suma(cuentas.map((c) => suma(mesesDe(c))));
  const totalReal = suma(cuentas.map((c) => suma(c.real)));

  const headers = ['Código', 'Cuenta', ...MESES, 'Total presup.', 'Real del año', 'Variación'];
  const filasExport = useMemo<Celda[][]>(
    () => cuentas.map((c) => {
      const m = mesesDe(c); const t = suma(m); const r = suma(c.real);
      return [c.codigo, c.nombre, ...m, t, r, t - r] as Celda[];
    }),
    // mesesDe depende de `edit`, que sí está en las dependencias
    [cuentas, edit], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Target className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Presupuesto</h1>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <label className="flex flex-col text-xs text-muted-foreground">Ejercicio
            <input type="number" className={`${field} mt-1 w-28`} value={anio}
              onChange={(e) => setAnio(Number(e.target.value))} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Cuentas
            <select className={`${field} mt-1 w-56`} value={universo} onChange={(e) => setUniverso(e.target.value)}>
              {UNIVERSOS.map((u) => <option key={u.v} value={u.v}>{u.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Buscar
            <input className={`${field} mt-1 w-64`} placeholder="código o nombre" value={filtro}
              onChange={(e) => setFiltro(e.target.value)} />
          </label>
          <Button size="sm" variant="outline" onClick={() => setModal(true)}>
            <Wand2 className="mr-1 h-4 w-4" /> Generar desde…
          </Button>
          <Button size="sm" variant="outline" disabled={!data?.cuentas?.length}
            title="Descarga un Excel con todas las cuentas y los 12 meses para llenarlo fuera del sistema"
            onClick={() => void descargarPlantilla(anio, universo, data?.cuentas ?? [])}>
            <FileDown className="mr-1 h-4 w-4" /> Plantilla Excel
          </Button>
          <Button size="sm" variant="outline" disabled={importando}
            title="Sube la plantilla llena; los importes quedan pendientes de guardar"
            onClick={() => fileRef.current?.click()}>
            {importando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileUp className="mr-1 h-4 w-4" />}
            Importar Excel
          </Button>
          <input ref={fileRef} type="file" className="hidden"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void importar(f); }} />
          <div className="ml-auto flex items-end gap-2 pb-1">
            <ExportarBotones nombre={`presupuesto-${anio}`} titulo={`Presupuesto ${anio}`}
              headers={headers} filas={filasExport}
              colsDerecha={Array.from({ length: 15 }, (_, i) => i + 2)}
              fmtPDF={(v, c) => (c < 2 ? String(v ?? '') : usd(Number(v ?? 0)))}
              disabled={isFetching} />
            <Button size="sm" disabled={!sucio || guardar.isPending} onClick={() => guardar.mutate()}>
              {guardar.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Guardar{sucio ? ` (${Object.keys(edit).length})` : ''}
            </Button>
          </div>
        </CardContent>
      </Card>

      {resImport && (
        <Card>
          <CardContent className="space-y-2 pt-6 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-estable-soft px-2 py-1 text-xs text-estable">
                {resImport.aplicadas} cuenta(s) cargadas del archivo
              </span>
              {resImport.enCero > 0 && (
                <span className="rounded-md bg-muted px-2 py-1 text-xs" title="Se guardarán como presupuesto cero, borrando lo que hubiera">
                  {resImport.enCero} vienen en cero
                </span>
              )}
              {resImport.noEncontradas.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5" /> {resImport.noEncontradas.length} código(s) sin cuenta que coincida
                </span>
              )}
              {resImport.sinCodigo > 0 && (
                <span className="rounded-md bg-muted px-2 py-1 text-xs">{resImport.sinCodigo} fila(s) sin código, omitidas</span>
              )}
              <button className="ml-auto text-xs underline decoration-dotted" onClick={() => setResImport(null)}>Ocultar</button>
            </div>
            {resImport.noEncontradas.length > 0 && (
              <div className="text-xs text-muted-foreground">
                No encontrados: <span className="font-mono">{resImport.noEncontradas.join(', ')}</span>.
                Suelen ser cuentas de otro universo (cambia el selector «Cuentas»), cuentas de grupo —solo se
                presupuestan las imputables— o códigos escritos a mano que no existen en el plan.
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Nada se ha guardado todavía: los importes están en la edición pendiente. Revísalos en la rejilla
              y pulsa <strong>Guardar</strong>.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-3 text-base">
            <span>{cuentas.length} cuenta(s)</span>
            {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              Presupuestado {usd(totalPresu)} · Real {usd(totalReal)}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cuentas.length === 0 && !isFetching && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No hay cuentas imputables que encajen con el filtro.
            </p>
          )}
          {cuentas.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="sticky left-0 z-10 bg-card py-2 pr-3 text-left">Cuenta</th>
                    {MESES.map((m) => <th key={m} className="px-1 py-2 text-right">{m}</th>)}
                    <th className="border-l px-2 py-2 text-right">Anual</th>
                    <th className="px-2 py-2 text-right">Real</th>
                  </tr>
                </thead>
                <tbody>
                  {cuentas.map((c) => {
                    const m = mesesDe(c); const t = suma(m); const r = suma(c.real);
                    const tocada = !!edit[c.cuenta_id];
                    return (
                      <tr key={c.cuenta_id} className={`border-b ${tocada ? 'bg-primary/5' : ''}`}>
                        <td className="sticky left-0 z-10 whitespace-nowrap bg-inherit py-1 pr-3">
                          <span className="font-mono text-xs text-muted-foreground">{c.codigo}</span>{' '}
                          <span title={c.nombre}>{c.nombre.slice(0, 34)}</span>
                        </td>
                        {m.map((v, i) => (
                          <td key={i} className="px-0.5 py-1">
                            <input
                              className="h-7 w-20 rounded border border-input bg-background px-1 text-right text-xs tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              defaultValue={v === 0 ? '' : String(v)}
                              onBlur={(e) => { const n = num(e.target.value); if (n !== v) setMes(c, i, n); }}
                            />
                          </td>
                        ))}
                        <td className="border-l px-2 py-1 text-right">
                          <input
                            className="h-7 w-24 rounded border border-input bg-background px-1 text-right text-xs font-medium tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            key={`t-${c.cuenta_id}-${t}`} defaultValue={t === 0 ? '' : String(t)}
                            title="Escribe aquí el total del año y se reparte entre los 12 meses"
                            onBlur={(e) => { const n = num(e.target.value); if (n !== t) repartirAnual(c, n); }}
                          />
                        </td>
                        <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted-foreground">
                          {usd(r)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/60 font-semibold">
                    <td className="sticky left-0 z-10 bg-inherit py-2 pr-3">Total</td>
                    {MESES.map((_, i) => (
                      <td key={i} className="px-1 py-2 text-right text-xs tabular-nums">
                        {usd(suma(cuentas.map((c) => mesesDe(c)[i] ?? 0)))}
                      </td>
                    ))}
                    <td className="border-l px-2 py-2 text-right tabular-nums">{usd(totalPresu)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{usd(totalReal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Los importes van en saldo natural: ingresos y gastos, ambos positivos. La columna <em>Anual</em> es
        editable — escribe el total del año y se reparte entre los doce meses. El presupuesto es por cuenta y
        mes, sin centro de costo ni proyecto, así que el comparativo de Inteligencia de negocios solo se
        ofrece cuando las columnas son de tiempo.
      </p>
      <p className="text-xs text-muted-foreground">
        Para llenarlo fuera del sistema: <strong>Plantilla Excel</strong> baja un archivo con todas las
        cuentas del universo elegido y sus doce meses (ya con lo que haya presupuestado, más el real del año
        como referencia). Llénalo e <strong>Importar Excel</strong> lo sube. Las cuentas se casan por
        <em> código</em>, así que puedes reordenar columnas o añadir las tuyas; lo único que no debes hacer es
        renombrar las cabeceras. La importación no guarda nada por su cuenta: deja los importes en la edición
        pendiente para que los revises y pulses Guardar.
      </p>

      {modal && (
        <GenerarModal anio={anio} onCerrar={() => setModal(false)}
          onHecho={() => void qc.invalidateQueries({ queryKey: ['presupuesto'] })} />
      )}
    </div>
  );
}
